import { forwardRef, useState } from "react";

import { IconStateInformation } from "@/assets/icons/figma/IconStateInformation";
import { IconStateWarningAlert } from "@/assets/icons/figma/IconStateWarningAlert";
import { cn } from "@/common/utils/css.utils";
import {
  BankAccountForm,
  BankAccountFormAccount,
} from "@/components/Account/payment/BankAccountForm";
import { usePaymentCopy } from "@/components/Account/payment/usePaymentCopy";
import { InfoPanel } from "@/ui/InfoPanel";
import { Button } from "@/ui/cva/Button";
import { Heading } from "@/ui/cva/Heading";
import { Body } from "@/ui/typography/Body";

export type BankAccountRowAccount = BankAccountFormAccount & {
  bank_name: string;
  status?: string | null;
};

type BankAccountSectionProps = {
  id: string;
  bankAccount: BankAccountRowAccount;
  /** No Stripe account yet: the account is only stored at Whoppah so far. */
  accountCreated: boolean;
  onSaved: () => Promise<unknown> | void;
  highlighted?: boolean;
};

const ICON_CLASS = "size-6 shrink-0";

/**
 * The compact row: bank and account holder, a Change link, and the form
 * only once Change is clicked. No account number on screen (fixed decision).
 * Absent from the page when the form already lives in the task above.
 */
export const BankAccountSection = forwardRef<
  HTMLElement,
  BankAccountSectionProps
>(({ id, bankAccount, accountCreated, onSaved, highlighted = false }, ref) => {
  const { copy } = usePaymentCopy();
  const [editing, setEditing] = useState(false);
  const errored = bankAccount.status === "errored";

  return (
    <section
      id={id}
      ref={ref}
      aria-labelledby={`${id}-title`}
      className={cn(
        "flex scroll-mt-24 flex-col gap-3 rounded-[0.25rem]",
        highlighted && "ring-2 ring-brand-500 ring-offset-4"
      )}
    >
      <Heading as="h2" size="h5" id={`${id}-title`}>
        {copy("sectionBankAccount")}
      </Heading>
      {errored && (
        <InfoPanel
          color="attention"
          icon={<IconStateWarningAlert className={ICON_CLASS} />}
          body={<p>{copy("bankErrored")}</p>}
        />
      )}
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-grey-200 pb-3">
        <Body as="p">
          <span className="font-bold">{bankAccount.bank_name}</span>
          {bankAccount.account_holder_name && (
            <>
              <span aria-hidden> · </span>
              <span>{bankAccount.account_holder_name}</span>
            </>
          )}
        </Body>
        {!editing && (
          <Button
            type="button"
            appearance="minimal"
            size="small"
            onClick={() => setEditing(true)}
          >
            {copy("bankChange")}
          </Button>
        )}
      </div>
      {!accountCreated && (
        <InfoPanel
          color="curious"
          icon={<IconStateInformation className={ICON_CLASS} />}
          body={<p>{copy("bankSavedAtWhoppah")}</p>}
        />
      )}
      {editing && (
        <BankAccountForm
          bankAccount={bankAccount}
          clearNumber={errored}
          onSaved={async () => {
            await onSaved();
            setEditing(false);
          }}
          onCancel={() => setEditing(false)}
        />
      )}
    </section>
  );
});

BankAccountSection.displayName = "BankAccountSection";
